import {
  createErrorBoundaryProps as baseCreateErrorBoundaryProps,
  handleMutationError as baseHandleMutationError,
  getErrorMessage,
  handleQueryError,
  isNetworkError,
} from '@repo/ui'
import { toast } from 'sonner'

/**
 * Enhanced mutation error handler with toast notifications
 */
export function handleMutationError(error: unknown, fallbackMessage: string): void {
  baseHandleMutationError(error, fallbackMessage)

  const errorMessage = getErrorMessage(error)
  toast.error(fallbackMessage, {
    description: errorMessage,
    duration: 5000,
  })
}

/**
 * Enhanced async error wrapper with toast notifications
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  fallbackMessage: string,
  onError?: (error: unknown) => void
): Promise<T | null> {
  try {
    return await operation()
  } catch (error) {
    handleMutationError(error, fallbackMessage)
    onError?.(error)
    return null
  }
}

/**
 * Enhanced error boundary props with toast integration
 */
export function createErrorBoundaryProps(error?: Error) {
  return {
    ...baseCreateErrorBoundaryProps(error),
    title: 'Something went wrong',
    description: 'We encountered an error while loading this section. Please try again.',
  }
}

// Re-export shared utilities
export { isNetworkError, getErrorMessage, handleQueryError }
